import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import type {
  CartridgeAssignmentResponse,
  CartridgeDetailResponse,
} from "@/types/cartridge";
import { queryKeys } from "@/lib/queryKeys";

export function useCartridgeDetail(name: string | null) {
  const query = useQuery({
    queryKey: queryKeys.cartridges.detail(name ?? ""),
    queryFn: () =>
      api<CartridgeDetailResponse>(
        `/api/v2/cartridges/${encodeURIComponent(name!)}`,
      ),
    enabled: name !== null,
    // 403 here means a visible public cartridge without an org install
    // (D2, S-KC4.2/S-KC4.4) — actionable, not a failure. Excluded from
    // retry via the global retry policy (401/403/404 never retry).
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  const is403 = query.error instanceof ApiError && query.error.status === 403;

  return {
    detail: query.data ?? null,
    loading: query.isLoading,
    error: !is403 && query.error ? "Failed to load cartridge detail" : "",
    notInstalled: is403,
    refetch,
  };
}

export function useCartridgeAssignments(name: string | null) {
  const query = useQuery({
    queryKey: queryKeys.cartridges.assignments(name ?? ""),
    queryFn: () =>
      api<CartridgeAssignmentResponse[]>(
        `/api/v2/cartridges/${encodeURIComponent(name!)}/assignments`,
      ),
    enabled: name !== null,
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    assignments: query.data ?? [],
    loading: query.isLoading,
    error: query.error ? "Failed to load assignments" : "",
    refetch,
  };
}
